import { jsx as _jsx } from "react/jsx-runtime";
import { ZERO } from '@/lib';
import { addGasMargin } from '@/utils';
import { BigNumber } from 'ethers';
import { createContext, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useMaskboxContract } from '..';
import { useWeb3Context } from '../Web3Context';
export * from './useAddress';
export * from './useContract';
export const MBoxContractContext = createContext({
    getBoxInfo: () => Promise.resolve({}),
    getBoxStatus: () => Promise.resolve({}),
    getNftListForSale: () => Promise.resolve([]),
    openBox: () => Promise.resolve(null),
    getPurchasedNft: () => Promise.resolve([]),
});
export const useMBoxContract = () => useContext(MBoxContractContext);
export function usePurchasedNft(boxId, customer) {
    const { getPurchasedNft } = useMBoxContract();
    const [ids, setIds] = useState([]);
    useEffect(() => {
        if (!boxId || !customer)
            return;
        getPurchasedNft(boxId, customer).then(setIds);
    }, [boxId, customer, getPurchasedNft]);
    return ids;
}
export const MBoxContractProvider = memo(({ children }) => {
    const { ethersProvider } = useWeb3Context();
    const contract = useMaskboxContract();
    const getBoxInfo = useCallback(async (boxId) => {
        if (!contract)
            return;
        const boxInfo = await contract.getBoxInfo(boxId);
        return boxInfo;
    }, [contract]);
    const getBoxStatus = useCallback(async (boxId) => {
        if (!contract)
            return;
        const boxStatus = await contract.getBoxStatus(boxId);
        return boxStatus;
    }, [contract]);
    const getNftListForSale = useCallback(async (boxId, cursor = ZERO, amount = BigNumber.from(20)) => {
        if (!contract)
            return [];
        const idList = await contract.getNftListForSale(boxId, cursor, amount);
        return idList.map((id) => id.toString());
    }, [contract]);
    const openBox = useCallback(async (boxId, quantity, paymentTokenIndex, proof, overrides) => {
        if (!contract || !ethersProvider) {
            return null;
        }
        const signer = ethersProvider.getSigner();
        const estimatedGas = await contract
            .connect(signer)
            .estimateGas.openBox(boxId, quantity, paymentTokenIndex, proof, overrides);
        const tx = await contract.connect(signer).openBox(boxId, quantity, paymentTokenIndex, proof, {
            gasLimit: addGasMargin(estimatedGas),
            ...overrides,
        });
        return tx;
    }, [contract]);
    const getPurchasedNft = useCallback(async (boxId, customer) => {
        if (!contract)
            return [];
        const idList = await contract.getPurchasedNft(boxId, customer);
        return idList.map((bn) => bn.toString());
    }, [contract]);
    const contextValue = {
        getBoxInfo,
        getBoxStatus,
        getNftListForSale,
        openBox,
        getPurchasedNft,
    };
    return (_jsx(MBoxContractContext.Provider, { value: contextValue, children: children }));
});
